import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import { Label } from '@/components/ui/catalyst/fieldset';
import type { CompanionMember } from '@/services/types/companions-types';

export interface CompanionMatterPickerProps {
  companions: CompanionMember[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  label?: string;
  disabled?: boolean;
}

export const CompanionMatterPicker = ({
  companions,
  selectedIds,
  onChange,
  label = 'Companion Matters',
  disabled = false,
}: CompanionMatterPickerProps) => {
  if (companions.length === 0) return null;

  const allSelected = selectedIds.length === companions.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const handleSelectAll = (checked: boolean) =>
    onChange(checked ? companions.map((c) => c.id) : []);

  const handleToggle = (id: string, checked: boolean) =>
    onChange(
      checked ? [...selectedIds, id] : selectedIds.filter((x) => x !== id),
    );

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-zinc-900 dark:text-white">
        {label}
      </p>
      <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-zinc-200 bg-zinc-50 p-2 dark:border-zinc-700 dark:bg-zinc-800">
        {/* Select All */}
        <CheckboxField>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={handleSelectAll}
            disabled={disabled}
          />
          <Label className="text-xs">Select All</Label>
        </CheckboxField>
        <div className="border-t border-zinc-200 dark:border-zinc-700" />
        {/* Individual matters */}
        {companions.map((c) => (
          <CheckboxField key={c.id}>
            <Checkbox
              checked={selectedIds.includes(c.id)}
              onChange={(v: boolean) => handleToggle(c.id, v)}
              disabled={disabled}
            />
            <Label className="text-xs">{c.display_name}</Label>
          </CheckboxField>
        ))}
      </div>
    </div>
  );
};
